'use client'

import { Client, getClient } from '@/lib/api/clients/client-api'
import {
    Allergy,
    getAllergiesByClient,
} from '@/lib/api/prontuario/prontuario-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdPerson, MdWarning } from 'react-icons/md'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ProntuarioTab, PRONTUARIO_TABS } from './prontuario-tabs'

export type { ProntuarioContext } from './prontuario-context'

const tabs = PRONTUARIO_TABS

export default function ProntuarioLayout() {
    const navigate = useNavigate()
    const location = useLocation()
    const { clientId } = useParams<{ clientId: string }>()
    const { clientLabel } = useClientLabel()

    /**
     * Guarda da ROTA, não só do botão.
     *
     * Esconder o botão no cadastro do paciente não impede digitar a URL, e desde que
     * o módulo `medical` saiu do menu este é o único caminho para receituário e
     * atestados — o que torna esta a porta que a permissão precisa fechar. O layout é
     * onde a checagem cabe: envolve TODAS as abas, então nenhuma sub-rota escapa.
     *
     * O backend continua sendo a trava de verdade; aqui é para não abrir uma tela
     * vazia de erros a quem não deveria vê-la.
     */
    const { canRead: canReadMedicalRecord } = useResourcePermissions(
        'medical',
        'medical-records'
    )

    const [client, setClient] = useState<Client | null>(null)
    const [allergies, setAllergies] = useState<Allergy[]>([])
    const [loading, setLoading] = useState(true)

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (!canReadMedicalRecord) {
            toast.error('Você não tem acesso ao prontuário do paciente')
            navigate('/person/clients', { replace: true })
            return
        }
        if (clientId) {
            loadData()
        }
    }, [clientId, canReadMedicalRecord])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const [clientResponse, allergiesResponse] = await Promise.all([
                getClient(parseInt(clientId, 10)),
                getAllergiesByClient(parseInt(clientId, 10)),
            ])

            if (clientResponse.success && clientResponse.result) {
                setClient(clientResponse.result)
            } else {
                toast.error(
                    clientResponse.error || `Erro ao carregar ${clientLabel}`
                )
                navigate('/person/clients')
                return
            }

            if (allergiesResponse.success) {
                setAllergies(allergiesResponse.result || [])
            }
        } catch (error) {
            toast.error(`Erro ao carregar dados do ${clientLabel}`)
            navigate('/person/clients')
        } finally {
            setLoading(false)
        }
    }

    const getCurrentTab = (): string => {
        const currentPath = location.pathname.replace(basePath, '')
        if (!currentPath || currentPath === '/') return 'resumo'
        const tab = tabs.find(
            t => t.path !== '' && currentPath.startsWith(t.path)
        )
        return tab?.id || 'resumo'
    }

    const handleTabChange = (tab: ProntuarioTab) => {
        navigate(`${basePath}${tab.path}`)
    }

    const activeAllergiesCount = allergies.filter(
        a => a.status === 'ACTIVE'
    ).length
    const hasActiveAllergies = activeAllergiesCount > 0

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando prontuário...
                </p>
            </div>
        )
    }

    if (!client) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdPerson className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Paciente não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O paciente que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/person/clients')}
                    className="btn btn-secondary"
                >
                    Voltar para {clientLabel}s
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4 p-4 max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate(`/person/clients/${clientId}`)
                                }
                                className="btn btn-ghost btn-circle btn-sm"
                            >
                                <MdArrowBack className="text-xl" />
                            </button>

                            <div className="flex items-center gap-4">
                                {client.photoUrl ? (
                                    <img
                                        src={client.photoUrl}
                                        alt={client.name}
                                        className="w-14 h-14 rounded-xl object-cover shadow-lg"
                                    />
                                ) : (
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                                        <MdPerson className="text-2xl text-white" />
                                    </div>
                                )}

                                <div>
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-xl font-bold text-base-content">
                                            {client.name}
                                        </h1>
                                        {hasActiveAllergies && (
                                            <div
                                                className="tooltip tooltip-right"
                                                data-tip={`${activeAllergiesCount} alergia(s) ativa(s)`}
                                            >
                                                <span className="badge badge-error gap-1 text-white">
                                                    <MdWarning className="text-sm" />
                                                    {activeAllergiesCount}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-sm text-base-content/60">
                                        Prontuário Eletrônico
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() =>
                                    navigate(`/person/clients/${clientId}`)
                                }
                                className="btn btn-ghost btn-sm"
                            >
                                Ver Cadastro
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-hidden">
                <div className="tabs tabs-bordered flex-wrap px-2 py-2">
                    {tabs.map(tab => {
                        const isActive = getCurrentTab() === tab.id
                        const TabIcon = tab.icon
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab)}
                                className={`tab gap-2 ${
                                    isActive
                                        ? 'tab-active text-secondary font-semibold'
                                        : 'text-base-content/70 hover:text-base-content'
                                }`}
                            >
                                <TabIcon className="text-lg" />
                                <span className="hidden sm:inline">
                                    {tab.label}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Content */}
            <div className="min-h-[400px]">
                <Outlet
                    context={{ client, allergies, reloadAllergies: loadData }}
                />
            </div>
        </div>
    )
}
